// src/services/api.js
import axios from "axios";
import qs from "qs";
import { toast } from "react-toastify";
import { getAccessToken, clearAccessToken } from "../utils/authStorage";

/**
 * Unified API client for the frontend.
 * - Base URL controlled by VITE_API_URL (set to 'http://localhost:3001/api/v1' by default)
 * - JSON by default (Content-Type: application/json)
 * - Supports sending application/x-www-form-urlencoded when explicitly set
 * - withCredentials: true (cookies/session if your backend uses them)
 */

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  paramsSerializer: (params) =>
    qs.stringify(params, { arrayFormat: "brackets" }),
});

// Attach token if present and convert data when form-urlencoded is requested
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // Mandatory Security Headers: Unique Request ID and Timestamp for Anti-Replay
    config.headers["x-request-id"] =
      window.crypto?.randomUUID?.() ||
      Date.now().toString(36) + Math.random().toString(36).substring(2);
    config.headers["x-timestamp"] = Date.now().toString();

    // Zero Trust: Device Fingerprint binding
    const fingerprint = localStorage.getItem("x-device-fingerprint");
    if (fingerprint) config.headers["x-device-fingerprint"] = fingerprint;

    // Zero Trust: CSRF Protection (Double-Submit Cookie)
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(";").shift();
    };
    const csrfToken =
      getCookie("x-csrf-token-secret") ||
      localStorage.getItem("x-csrf-token-fallback");
    if (csrfToken) config.headers["x-csrf-token"] = csrfToken;

    // If the caller explicitly sets Content-Type to application/x-www-form-urlencoded,
    // serialize the request body using qs.
    const contentType =
      config.headers &&
      (config.headers["Content-Type"] || config.headers["content-type"]);
    if (
      contentType &&
      typeof config.data === "object" &&
      contentType.includes("application/x-www-form-urlencoded")
    ) {
      config.data = qs.stringify(config.data);
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Centralized response handling
api.interceptors.response.use(
  (response) => {
    // Zero Trust: Capture rotated CSRF token from server headers
    const csrfHeader = response.headers["x-csrf-token"];
    if (csrfHeader) {
      localStorage.setItem("x-csrf-token-fallback", csrfHeader);
    }
    return response;
  },
  (error) => {
    // No response -> network error
    if (!error.response) {
      toast.error("Network error. Please check your connection.");
      return Promise.reject(error);
    }

    const { status, data } = error.response;

    if (status === 401) {
      // Unauthorized: clear token so front-end knows
      clearAccessToken();
      // Optionally redirect to login here if desired:
      // window.location.href = '/login';
    } else if (status === 400) {
      toast.error(data?.message || "Bad request. Check your input.");
    } else if (status === 403) {
      toast.error("Forbidden. You are not authorized to perform this action.");
    } else if (status >= 500) {
      toast.error("Server error. Try again later.");
    }

    return Promise.reject(error);
  },
);

/**
 * Helper: send OTP
 * Returns response.data on success, throws Error on failure (message populated)
 */
export const sendOtp = async (email, purpose = "login") => {
  try {
    const res = await api.post("/auth/send-otp", { email, purpose });
    return res.data;
  } catch (err) {
    const msg =
      err.response?.data?.message || err.message || "Failed to send OTP";
    throw new Error(msg);
  }
};

/**
 * Helper: verify OTP
 * Returns response.data on success, throws Error on failure (message populated)
 */
export const verifyOtp = async (email, otp, purpose = "login") => {
  try {
    const res = await api.post("/auth/verify-otp", { email, otp, purpose });
    return res.data;
  } catch (err) {
    const msg =
      err.response?.data?.message || err.message || "Failed to verify OTP";
    throw new Error(msg);
  }
};

/**
 * Helper: update Avatar
 * Returns response.data on success, throws Error on failure
 */
export const updateAvatar = async (avatarUrl) => {
  try {
    const res = await api.put("/users/profile", { avatar: avatarUrl });
    return res.data;
  } catch (err) {
    const msg =
      err.response?.data?.message || err.message || "Failed to update avatar";
    throw new Error(msg);
  }
};

export const validateVoucher = async (code) => {
  try {
    const res = await api.post("/vouchers/validate", { code });
    return res.data;
  } catch (err) {
    const msg =
      err.response?.data?.message || err.message || "Failed to validate voucher";
    throw new Error(msg);
  }
};

export default api;
